/**
 * Spintax v2 Synonym Dictionary
 * Single source of truth for all word/phrase replacements
 * 
 * IMPORTANT: This is the ONLY place where synonyms are defined.
 * Changes here automatically apply to both frontend and backend.
 * 
 * Structure:
 * - Keys are words or phrases to match
 * - Values are arrays of alternatives (synonyms/variations)
 * - Longer phrases are matched first (before individual words)
 * 
 * @module spintax-v2/synonymMap
 * @version 2.0.0
 */

/**
 * Main synonym dictionary
 * @type {Object.<string, string[]>}
 */
const synonymMap = {
  // ============================================
  // GREETINGS (capitalized for email etiquette)
  // ============================================
  "Hi": ["Hi", "Hello", "Hey", "Greetings"],
  "Hello": ["Hi", "Hey", "Greetings", "Good day"],
  "Hey": ["Hi", "Hello", "Greetings"],
  "hope you're well": [
    "hope you're doing well",
    "hope all is well",
    "trust you're good",
    "hope things are going well on your end"
  ],
  "I hope you're well": [
    "Hope you're doing well",
    "Hope everything's good with you",
    "Hope you're keeping well",
    "Trust you're well"
  ],

  // ============================================
  // PRONOUNS (safe only - avoid ambiguous replacements)
  // ============================================
  "I": ["I"],
  "we": ["we", "our team"],
  "you": ["you", "your team"],
  "your": ["your", "your team's"],
  "they": ["they", "their team"],
  "their": ["their", "their team's"],
  "me": ["me", "myself"],
  "us": ["us", "our team"],
  "them": ["them", "their team"],
  "mine": ["mine", "my own"],
  "ours": ["ours", "our own"],
  "theirs": ["theirs", "their own"],

  // ============================================
  // COMMON VERBS — safe as single words and phrases
  // ============================================
  "want": ["would like", "am hoping to", "am looking to"],
  "need": ["require", "am in need of", "am hoping for"],
  "send": ["forward", "share", "send over"],
  "receive": ["get", "obtain", "be sent"],
  "reply": ["respond", "get back to me", "reach out"],
  "talk": ["chat", "speak", "discuss"],
  "email": ["message", "note", "email"],
  "follow up": ["check in", "follow up", "touch base"],
  "ask": ["ask", "inquire", "request"],
  "check": ["check", "review", "look over"],
  "help": ["help", "assist", "support"],
  "try": ["try", "attempt", "test"],
  "reach": ["reach", "contact", "approach"],
  "start": ["start", "begin", "kick off"],
  "update": ["update", "inform", "notify"],
  "join": ["join", "participate", "attend"],
  "work": ["work", "collaborate", "partner"],
  "find": ["find", "discover", "identify"],

  // ============================================
  // MODAL / TONE ADJUSTERS
  // ============================================
  "can": ["can", "could", "might be able to"],
  "should": ["should", "might want to", "may want to"],
  "would": ["would", "might", "could"],
  "will": ["will", "is going to", "plans to"],
  "may": ["may", "might", "could"],
  "might": ["might", "could", "possibly"],
  "must": ["must", "should", "need to"],
  "shall": ["shall", "will", "plan to"],
  "try to": ["try to", "aim to", "hope to"],

  // ============================================
  // POLITENESS / SOFTENING
  // ============================================
  "just": ["just", "simply", "really", "quickly"],
  "really": ["truly", "genuinely", "definitely"],
  "very": ["extremely", "highly", "particularly"],
  "a quick": ["a brief", "a short", "a quick little"],
  "quick question": ["short question", "quick follow-up", "brief question"],
  "hopefully": ["hopefully", "ideally", "with luck"],
  "appreciate": ["appreciate", "value", "grateful for"],
  "sorry": ["sorry", "apologies", "regret"],
  "small": ["small", "quick", "brief"],
  "nice": ["nice", "pleasant", "great"],
  "simple": ["simple", "straightforward", "basic"],
  "kind": ["kind", "friendly", "generous"],
  "happy": ["happy", "glad", "pleased"],
  "easy": ["easy", "simple", "smooth"],
  "soon": ["soon", "shortly", "quickly"],
  "important": ["important", "notable", "key", "critical"],

  // ============================================
  // CONNECTORS & POLITE CLOSINGS
  // ============================================
  "let me know": [
    "let me know",
    "feel free to let me know",
    "I'd love to hear your thoughts",
    "please don't hesitate to reach out"
  ],
  "get in touch": ["reach out", "contact me", "drop me a line"],
  "thanks": ["thanks", "thank you", "many thanks", "much appreciated"],
  "thank you": ["thank you", "thanks", "much appreciated", "truly appreciate it"],
  "looking forward to": [
    "looking forward to",
    "excited about",
    "can't wait to",
    "eager to"
  ],
  "as soon as possible": [
    "as soon as possible",
    "at your earliest convenience",
    "when you have a moment",
    "whenever you're free"
  ],

  // ============================================
  // CAMPAIGN-SPECIFIC (Booking Agency & General Event Use)
  // ============================================
  "venue": ["venue", "club", "location", "space", "spot"],
  "book": ["book", "schedule", "arrange", "set up"],
  "show": ["show", "performance", "gig", "set"],
  "availability": ["availability", "open dates", "slots", "times"],
  "booking": ["booking", "scheduling", "reserving"],
  "set": ["set", "performance", "slot", "appearance"],
  "gigging": ["gigging", "playing", "performing", "touring"],
  "perform": ["perform", "play", "appear", "headline"],
  "tour": ["tour", "run", "string", "series"],
  "dates": ["dates", "slots", "schedule", "openings"],
  "soundcheck": ["soundcheck", "setup", "prep", "rehearsal"],
  "support": ["support", "open", "join", "warm up"],
  "slot": ["slot", "set", "window", "segment"],
  "stage": ["stage", "floor", "platform", "area"],
  "artist": ["artist", "act", "performer", "musician"],

  // ============================================
  // CAMPAIGN-RELATED FULL PHRASES (optional trigger boost)
  // ============================================
  "interested in booking": [
    "looking to book",
    "hoping to schedule",
    "planning a performance",
    "keen to perform"
  ],
  "we're looking to book shows": [
    "we're hoping to secure gigs",
    "we'd love to book some performances",
    "we're arranging a tour and looking for venues",
    "we're planning shows and looking for venues"
  ],
  "just checking if you have any availability": [
    "wondering if you've got any dates available",
    "hoping to see if your venue has any space soon",
    "checking if there are any open slots",
    "seeing if you're taking bookings at the moment"
  ],
  "let us know if that sounds interesting": [
    "we'd love to hear your thoughts",
    "happy to chat more if you're open",
    "feel free to reply if that aligns",
    "let me know if you'd like to discuss"
  ]
};

/**
 * Category-based organization for easier management
 * (Optional - can be used for UI grouping or targeted replacement)
 * @type {Object.<string, string[]>}
 */
const synonymCategories = {
  greetings: ["Hi", "Hello", "Hey"],
  venues: ["venue", "book", "show", "availability", "booking", "perform", "tour"],
  politeness: ["thanks", "thank you", "appreciate", "looking forward to", "let me know"],
  modals: ["can", "should", "would", "will", "may", "might", "must"],
  connectors: ["just", "really", "very", "hopefully"],
  closings: ["get in touch", "as soon as possible"]
};

/**
 * Get all keys sorted by length (longest first)
 * This ensures phrases are matched before individual words
 * @returns {string[]} Sorted array of synonym keys
 */
function getSortedKeys() {
  return Object.keys(synonymMap).sort((a, b) => b.length - a.length);
}

/**
 * Get synonyms for a specific word/phrase
 * @param {string} key - Word or phrase to look up
 * @returns {string[]|null} Array of synonyms or null if not found
 */
function getSynonyms(key) {
  return synonymMap[key] || null;
}

/**
 * Check if a word/phrase has synonyms defined
 * @param {string} key - Word or phrase to check
 * @returns {boolean} True if synonyms exist
 */
function hasSynonyms(key) {
  return key in synonymMap;
}

/**
 * Get total count of synonym entries
 * @returns {number} Number of entries in the dictionary
 */
function getEntryCount() {
  return Object.keys(synonymMap).length;
}

/**
 * Get total count of all synonym variations
 * @returns {number} Total number of variations across all entries
 */
function getVariationCount() {
  return Object.values(synonymMap).reduce((total, arr) => total + arr.length, 0);
}

// Export for both CommonJS (Node.js) and ES6 (React)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    synonymMap,
    synonymCategories,
    getSortedKeys,
    getSynonyms,
    hasSynonyms,
    getEntryCount,
    getVariationCount
  };
}

// ES6 export
export {
  synonymMap,
  synonymCategories,
  getSortedKeys,
  getSynonyms,
  hasSynonyms,
  getEntryCount,
  getVariationCount
};

export default synonymMap;

